import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, PlayCircle, Search, LogOut as CloseIcon, Ticket, Banknote, Smartphone, Wallet, ClipboardList, Plus, BusFront, Printer, History, XCircle, SearchCheck, HandCoins, Check, X, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import TripCard from "@/components/TripCard";
import BookingFlow from "@/components/BookingFlow";
import TicketView from "@/components/TicketView";
import PrintButtons from "@/components/PrintButtons";
import { PrintPortal } from "@/lib/print";
import TripsAdmin from "@/pages/admin/TripsAdmin";
import SettlementsAdmin from "@/pages/admin/SettlementsAdmin";
import ReportDialog from "@/components/ReportDialog";
import { StatCard, StatusBadge } from "@/components/Ui";
import ConsoleHeader from "@/components/ConsoleHeader";
import { VoiceButton } from "@/components/VoiceButton";
import { useStations } from "@/components/SearchForm";
import { BookingsTable } from "@/pages/admin/BookingsAdmin";
import ManifestDialog from "@/pages/admin/ManifestDialog";
import { CancelForm, CancelReceipt } from "@/pages/admin/TicketDesk";
import PaymentCheck from "@/pages/admin/PaymentCheck";
import TellerCancellations from "@/components/TellerCancellations";
import TransactionLedger from "@/components/TransactionLedger";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { money, fmtDate, fmtTime, fmtDateTime, todayStr, statusStyle } from "@/lib/format";

function ShiftHistory({ onOpen }) {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/teller/shifts").then((r) => setList(r.data)); }, []);
  if (!list.length) return null;
  return (
    <div className="card-soft divide-y divide-slate-100" data-testid="shift-history">
      <div className="label-xs p-4">My recent shifts</div>
      {list.map((s) => (
        <div key={s.id} className="flex items-center gap-3 p-4 text-sm">
          <span className="font-mono font-bold">{s.number}</span>
          <span className="flex-1 text-slate-500">{fmtDateTime(s.opened_at)} → {s.closed_at ? fmtDateTime(s.closed_at) : "open"}</span>
          <span className="font-semibold">{money(s.expected_cash)}</span>
          <StatusBadge status={s.status} cls={statusStyle[s.status]} />
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => onOpen(s.id)} data-testid={`shift-report-button-${s.id}`}>Report</Button>
        </div>
      ))}
    </div>
  );
}

function OpenShift({ user, onOpened, onReport }) {
  const stations = useStations();
  const st = user.station_id || "";
  const station = stations.find((s) => s.id === st);
  const open = async () => {
    try { await api.post("/teller/shift/open", {}); toast.success("Shift opened - balance GH₵ 0.00"); onOpened(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mx-auto max-w-2xl space-y-5 px-4 py-10">
      <div className="card-soft rise p-6 text-center">
        <PlayCircle className="mx-auto h-12 w-12 text-[#0052FF]" />
        <h1 className="mt-3 font-display text-2xl font-extrabold">Start your shift, {user.name.split(" ")[0]}</h1>
        {st ? (
          <>
            <p className="text-sm text-slate-500">Your counter balance starts at <b>GH₵ 0.00</b>. Everything you collect is tracked until you hand over to the cashier.</p>
            <div className="mx-auto mt-5 max-w-sm space-y-3 text-left">
              <Label className="text-xs font-semibold text-slate-600">Selling station</Label>
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700" data-testid="shift-station-fixed">{station ? `${station.city} - ${station.name}` : "Your home station"}</div>
              <Button onClick={open} className="h-12 w-full rounded-xl bg-[#0052FF] text-base font-bold hover:bg-[#0041CC] btn-press" data-testid="open-shift-button">Open shift</Button>
            </div>
          </>
        ) : (
          <div className="mx-auto mt-5 max-w-sm rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm font-medium text-amber-800" data-testid="no-station-warning">
            No home station is assigned to your account. Please ask an administrator to set your home station in User Master before you can start a shift.
          </div>
        )}
      </div>
      <ShiftHistory onOpen={onReport} />
    </div>
  );
}

function CloseShiftDialog({ shift, open, onClose, onClosed }) {
  const [cash, setCash] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [target, setTarget] = useState("cashier");
  const [tellerId, setTellerId] = useState("");
  const [tellers, setTellers] = useState([]);
  useEffect(() => { if (open) api.get("/teller/tellers").then((r) => setTellers(r.data)).catch(() => setTellers([])); }, [open]);
  const diff = Number(cash || 0) - shift.expected_cash;
  const submit = async () => {
    if (target === "teller" && !tellerId) return toast.error("Pick the next-duty teller to hand the cash to");
    setBusy(true);
    try {
      const { data } = await api.post("/teller/shift/close", { declared_cash: Number(cash), notes, handover_to: target, handover_teller_id: target === "teller" ? tellerId : null });
      onClosed(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl sm:max-w-md" data-testid="close-shift-dialog">
        <DialogHeader><DialogTitle className="font-display">Close shift {shift.number}</DialogTitle></DialogHeader>
        <div className="space-y-2 rounded-2xl bg-slate-50 p-4 text-sm">
          {[["Tickets sold", shift.tickets], ["Cash sales", money(shift.cash_total)], ["Cash received from other tellers", money(shift.carry_in_total || 0)], ["Mobile Money", money(shift.momo_total)], ["Card", money(shift.card_total)], ["Refunds", `- ${money(shift.refund_total)}`], ["Cancellation charges kept", money(shift.cancel_fees_total || 0)]].map(([k, v]) => (
            <div key={k} className="flex justify-between"><span>{k}</span><b>{v}</b></div>
          ))}
          <div className="flex justify-between border-t border-slate-200 pt-2 text-base"><span>Cash to hand over</span><b className="text-[#0052FF]" data-testid="close-shift-expected">{money(shift.expected_cash)}</b></div>
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-semibold text-slate-600">Hand the full cash over to</Label>
          <div className="grid grid-cols-2 gap-2">
            {[["cashier", "Cashier"], ["teller", "Next-duty teller"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setTarget(v)} data-testid={`handover-target-${v}`}
                className={`btn-press rounded-xl border px-3 py-2.5 text-sm font-bold ${target === v ? "border-[#0052FF] bg-[#0052FF] text-white" : "border-slate-200 bg-white text-slate-600"}`}>{l}</button>
            ))}
          </div>
          {target === "teller" && (
            <Select value={tellerId} onValueChange={setTellerId}>
              <SelectTrigger data-testid="handover-teller-select"><SelectValue placeholder="Select the teller taking over" /></SelectTrigger>
              <SelectContent>{tellers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
        <Label className="text-xs font-semibold text-slate-600">Cash counted in your drawer</Label>
        <Input value={cash} onChange={(e) => setCash(e.target.value.replace(/[^\d.]/g, ""))} inputMode="decimal" className="h-12 text-lg font-bold" placeholder="0.00" data-testid="declared-cash-input" />
        {cash !== "" && <div className={`rounded-xl p-2.5 text-sm font-bold ${Math.abs(diff) < 0.01 ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{Math.abs(diff) < 0.01 ? "Balanced" : diff > 0 ? `Over by ${money(diff)}` : `Short by ${money(-diff)}`}</div>}
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" data-testid="close-shift-notes-input" />
        <Button disabled={busy || cash === ""} onClick={submit} className="h-12 rounded-xl bg-[#0052FF] font-bold hover:bg-[#0041CC]" data-testid="confirm-close-shift-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : target === "teller" ? "Close & hand to teller" : "Close shift & print handover slip"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function IncomingHandovers({ onDone }) {
  const [list, setList] = useState([]);
  const load = useCallback(() => api.get("/teller/handovers").then((r) => setList(r.data)).catch(() => setList([])), []);
  useEffect(() => { load(); }, [load]);
  const ack = async (id) => {
    try { const { data } = await api.post(`/teller/handovers/${id}/acknowledge`); toast.success(`Received ${money(data.amount)} into your drawer`); load(); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!list.length) return null;
  return (
    <div className="card-soft space-y-2 border-violet-200 bg-violet-50/60 p-4" data-testid="incoming-handovers">
      <div className="label-xs text-violet-700">Cash handovers waiting for you</div>
      {list.map((s) => (
        <div key={s.id} className="flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`incoming-handover-${s.id}`}>
          <span>From <b>{s.teller_name}</b> · shift {s.number} · <b>{money(s.declared_cash ?? s.expected_cash)}</b></span>
          <Button size="sm" className="rounded-full bg-violet-600 hover:bg-violet-700" onClick={() => ack(s.id)} data-testid={`ack-handover-${s.id}`}>Acknowledge & take cash</Button>
        </div>
      ))}
    </div>
  );
}

function useTellerLists() {
  const [l, setL] = useState({ stations: [], routes: [], buses: [], drivers: [], bus_types: [], owners: [] });
  useEffect(() => {
    Promise.all(["stations", "routes", "buses", "drivers", "bus-types", "owner-options"].map((k) => api.get(`/admin/${k}`).then((r) => r.data).catch(() => [])))
      .then(([stations, routes, buses, drivers, bus_types, owners]) => setL({ stations, routes, buses, drivers, bus_types, owners }));
  }, []);
  const city = (id) => l.stations.find((s) => s.id === id)?.city || "-";
  return { ...l, city };
}

function AutoPrint({ booking, onDone }) {
  useEffect(() => {
    if (!booking) return;
    let el = document.getElementById("page-size-style");
    if (!el) { el = document.createElement("style"); el.id = "page-size-style"; document.head.appendChild(el); }
    el.textContent = "@page { size: 80mm auto; margin: 4mm 6mm; }";
    // Let the checkout dialog finish closing and repaint before the blocking window.print().
    const t = setTimeout(() => { window.print(); onDone(); }, 500);
    return () => clearTimeout(t);
  }, [booking]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!booking) return null;
  return <PrintPortal><TicketView booking={booking} format="thermal" /></PrintPortal>;
}

function IncomingCashTransfers({ onDone }) {
  const [list, setList] = useState([]);
  const load = useCallback(() => api.get("/teller/cash-transfers/incoming").then((r) => setList(r.data)).catch(() => setList([])), []);
  useEffect(() => { load(); }, [load]);
  const act = async (id, accept) => {
    try {
      if (accept) { const { data } = await api.post(`/cash-transfers/${id}/accept`); toast.success(`Received ${money(data.amount)} into your drawer`); }
      else { const reason = window.prompt("Why are you rejecting this cash?"); if (!reason) return; await api.post(`/cash-transfers/${id}/reject`, { reason }); toast.success("Rejected"); }
      load(); onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!list.length) return null;
  return (
    <div className="card-soft space-y-2 border-amber-200 bg-amber-50/60 p-4" data-testid="incoming-cash-transfers">
      <div className="label-xs text-amber-700">Cash handed to you by another teller</div>
      {list.map((c) => (
        <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`incoming-transfer-${c.id}`}>
          <span>From <b>{c.from_teller_name}</b> · <b>{money(c.amount)}</b></span>
          <div className="flex gap-2">
            <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => act(c.id, true)} data-testid={`accept-transfer-${c.id}`}><Check className="h-4 w-4" />Accept</Button>
            <Button size="sm" variant="outline" className="rounded-full border-rose-200 text-rose-600" onClick={() => act(c.id, false)} data-testid={`reject-transfer-${c.id}`}><X className="h-4 w-4" />Reject</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function GiveCashDialog({ open, onClose, onDone }) {
  const [kind, setKind] = useState("teller");
  const [tellers, setTellers] = useState([]);
  const [tellerId, setTellerId] = useState("");
  const [trips, setTrips] = useState([]);
  const [tripId, setTripId] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setKind("teller"); setTellerId(""); setTripId(""); setAmount(""); setNote("");
    api.get("/teller/tellers").then((r) => setTellers(r.data)).catch(() => setTellers([]));
    api.get("/teller/sm-transfer-trips", { params: { date: todayStr() } }).then((r) => setTrips(r.data)).catch(() => setTrips([]));
  }, [open]);
  const selTrip = trips.find((t) => t.id === tripId);
  const over = kind === "station_master" && selTrip && Number(amount) > selTrip.remaining + 0.001;
  const submit = async () => {
    const amt = Number(amount);
    if (!(amt > 0)) return toast.error("Enter an amount greater than zero");
    if (kind === "teller" && !tellerId) return toast.error("Select a teller to hand the cash to");
    if (kind === "station_master" && !tripId) return toast.error("Select the trip this cash is for");
    setBusy(true);
    try {
      await api.post("/teller/cash-transfer", { kind, to_teller_id: kind === "teller" ? tellerId : null, trip_id: kind === "station_master" ? tripId : null, amount: amt, note });
      toast.success("Cash handed over · waiting for the receiver to confirm");
      onDone(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl sm:max-w-md" data-testid="give-cash-dialog">
        <DialogHeader><DialogTitle className="font-display">Give cash</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          {[["teller", "To another teller"], ["station_master", "To station master"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setKind(v)} data-testid={`give-cash-kind-${v}`}
              className={`btn-press rounded-xl border px-3 py-2.5 text-sm font-bold ${kind === v ? "border-[#0052FF] bg-[#0052FF] text-white" : "border-slate-200 bg-white text-slate-600"}`}>{l}</button>
          ))}
        </div>
        {kind === "teller" ? (
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-600">Teller on duty (your station)</Label>
            <Select value={tellerId} onValueChange={setTellerId}>
              <SelectTrigger data-testid="give-cash-teller-select"><SelectValue placeholder="Select a teller" /></SelectTrigger>
              <SelectContent>{tellers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ) : (
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-600">Trip · Station-Master share</Label>
            <Select value={tripId} onValueChange={(v) => { setTripId(v); const t = trips.find((x) => x.id === v); if (t) setAmount(String(t.remaining)); }}>
              <SelectTrigger data-testid="give-cash-trip-select"><SelectValue placeholder="Select a trip" /></SelectTrigger>
              <SelectContent>{trips.length ? trips.map((t) => <SelectItem key={t.id} value={t.id}>{t.label} · left {money(t.remaining)}</SelectItem>) : <div className="px-3 py-2 text-xs text-slate-500">No trips with Station-Master cash</div>}</SelectContent>
            </Select>
            {selTrip && <p className="text-xs text-slate-500">Share {money(selTrip.sm_share)} · remaining {money(selTrip.remaining)}</p>}
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-slate-600">Amount (GH₵)</Label>
          <Input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))} inputMode="decimal" className="h-12 text-lg font-bold" placeholder="0.00" data-testid="give-cash-amount-input" />
          {over && <p className="text-xs font-semibold text-rose-600">Cannot exceed the remaining share {money(selTrip.remaining)}</p>}
        </div>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" rows={2} data-testid="give-cash-note-input" />
        <Button disabled={busy || over} onClick={submit} className="h-12 rounded-xl bg-[#0052FF] font-bold hover:bg-[#0041CC]" data-testid="give-cash-submit-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Hand over cash"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}


function LookupDialog({ open, onClose, onTicket, onChanged, shift, user }) {
  const [code, setCode] = useState("");
  const [b, setB] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const mine = b && b.teller_id === user?.id;
  const find = async () => {
    try { const { data } = await api.get(`/bookings/${code.trim().split("-")[0]}`); setB(data); } catch (e) { setB(null); toast.error(errMsg(e)); }
  };
  const reload = () => api.get(`/bookings/${b.code}`).then((r) => { setB(r.data); onChanged(); });
  const collect = async (method) => {
    try { const { data } = await api.post(`/bookings/${b.code}/collect`, { method }); toast.success("Payment collected"); onChanged(); onTicket(data); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); setB(null); setCode(""); } }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-3xl sm:max-w-2xl" data-testid="lookup-dialog">
        <DialogHeader><DialogTitle className="font-display">Find booking / collect reservation</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); find(); }} className="flex gap-2">
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Booking code e.g. GPAB12CD" className="font-mono uppercase" data-testid="lookup-code-input" />
          <Button type="submit" className="bg-[#0052FF] hover:bg-[#0041CC]" data-testid="lookup-search-button"><Search className="h-4 w-4" /></Button>
        </form>
        {b && (
          <div className="space-y-3">
            <BookingsTable list={[b]} reload={reload} />
            <div className="flex flex-wrap gap-2">
              {b.status === "reserved" && (
                <>
                  <Button onClick={() => collect("cash")} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="collect-cash-button"><Banknote className="h-4 w-4" />Collect {money(b.total)} cash</Button>
                  <Button onClick={() => collect("momo")} className="rounded-full bg-yellow-400 text-slate-900 hover:bg-yellow-300" data-testid="collect-momo-button"><Smartphone className="h-4 w-4" />Collect via MoMo</Button>
                </>
              )}
              {["confirmed", "boarded"].includes(b.status) && <Button variant="outline" className="rounded-full" onClick={() => { onTicket(b); onClose(); }} data-testid="reprint-ticket-button">Reprint ticket</Button>}
              {["confirmed", "reserved"].includes(b.status) && !cancelling && <Button variant="outline" className="rounded-full border-rose-200 text-rose-600" onClick={() => setCancelling(true)} data-testid="teller-cancel-button">Cancel ticket</Button>}
            </div>
            {cancelling && <CancelForm b={b} reasons={[...(mine ? [["teller_mistake", "Teller mistake", "0% - full refund, allowed within the admin time limit from booking."]] : []), ["customer_request", "Customer request", "Charge on fare by time to departure. Insurance returned in full."], ["trip_not_performed", "Trip not performed", "0% - bus did not run; fare + insurance returned (needs admin approval)."]]} onDone={(d) => { setB(d); setCancelling(false); onChanged(); }} />}
            {b.status === "cancelled" && <CancelReceipt booking={b} />}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SoldPanel({ booking, onNew }) {
  return (
    <div className="space-y-4" data-testid="sold-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
        <div><div className="font-display text-lg font-extrabold text-emerald-800">Ticket issued · {booking.code}</div><div className="text-sm text-emerald-700">{booking.passengers.length} seat(s) · {money(booking.total)} · SMS sent to passenger(s)</div></div>
        <Button onClick={onNew} className="rounded-full bg-[#0052FF] hover:bg-[#0041CC]" data-testid="new-sale-button"><Plus className="h-4 w-4" />New sale</Button>
      </div>
      <PrintButtons testPrefix="teller-ticket-print" size="default">{(f) => <TicketView booking={booking} format={f} />}</PrintButtons>
      <TicketView booking={booking} format="a4" />
    </div>
  );
}

function ReprintDialog({ booking, onClose }) {
  const cancelled = booking?.status === "cancelled";
  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-3xl sm:max-w-2xl" data-testid="reprint-dialog">
        <DialogHeader><DialogTitle className="font-display">{cancelled ? "Cancellation receipt" : "Reprint ticket"} · {booking?.code}</DialogTitle></DialogHeader>
        {booking && (cancelled ? <CancelReceipt booking={booking} /> : (
          <div className="space-y-4">
            <PrintButtons testPrefix="reprint-ticket-print" size="default">{(f) => <TicketView booking={booking} format={f} />}</PrintButtons>
            <TicketView booking={booking} format="a4" />
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
}

function ReprintScreen({ shift }) {
  const stations = useStations();
  const [origin, setOrigin] = useState(shift.station_id);
  const [date, setDate] = useState(todayStr());
  const [trips, setTrips] = useState([]);
  const [trip, setTrip] = useState(null);
  const [pax, setPax] = useState(null);
  const [printing, setPrinting] = useState(null);
  useEffect(() => {
    setTrip(null); setPax(null);
    api.get("/public/trips/search", { params: { origin_id: origin, date } }).then((r) => setTrips(r.data)).catch(() => setTrips([]));
  }, [origin, date]);
  const openTrip = async (t) => {
    setTrip(t); setPax(null);
    try { const { data } = await api.get(`/trips/${t.id}/manifest`); setPax(data.passengers); } catch (e) { toast.error(errMsg(e)); setPax([]); }
  };
  const print = async (code) => {
    try { const { data } = await api.get(`/bookings/${code}`); setPrinting(data); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid gap-5 lg:grid-cols-[360px_1fr]" data-testid="teller-reprint">
      <div className="space-y-3">
        <Select value={origin} onValueChange={setOrigin} disabled>
          <SelectTrigger data-testid="reprint-station-select"><SelectValue placeholder="Station" /></SelectTrigger>
          <SelectContent>{stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.city} - {s.name}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="reprint-date-input" />
        <div className="space-y-2" data-testid="reprint-trip-list">
          {trips.map((t) => (
            <button key={t.id} type="button" onClick={() => openTrip(t)} data-testid={`reprint-trip-${t.id}`}
              className={`card-soft btn-press flex w-full items-center gap-3 p-3 text-left ${t.id === trip?.id ? "ring-2 ring-[#0052FF]" : ""}`}>
              <span className="font-display text-lg font-extrabold">{fmtTime(t.departure)}</span>
              <span className="min-w-0 flex-1 text-sm"><b className="block truncate">{t.origin_city} → {t.destination_city}</b><span className="text-xs text-slate-500">{t.bus_type}{t.bus_number ? ` · Bus ${t.bus_number}` : ""}</span></span>
            </button>
          ))}
          {!trips.length && <p className="card-soft p-5 text-center text-sm text-slate-500">No trips from this station on this date.</p>}
        </div>
      </div>
      <div className="min-w-0">
        {!trip ? (
          <div className="card-soft grid h-80 place-items-center p-6 text-center text-slate-500"><div><Printer className="mx-auto h-10 w-10 text-blue-300" /><p className="mt-2 font-semibold">Select a trip to see its passengers</p></div></div>
        ) : pax === null ? <div className="grid h-40 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#0052FF]" /></div> : (
          <div className="card-soft divide-y divide-slate-100" data-testid="reprint-passenger-list">
            <div className="flex items-center justify-between p-4"><div className="label-xs">{trip.origin_city} → {trip.destination_city} · {fmtTime(trip.departure)}</div><span className="text-xs text-slate-500">{pax.length} passenger(s)</span></div>
            {pax.map((p) => (
              <div key={`${p.code}-${p.seat}`} className="flex items-center gap-3 p-4 text-sm" data-testid={`reprint-pax-${p.code}-${p.seat}`}>
                <span className="grid h-8 w-8 place-items-center rounded-lg bg-slate-100 text-sm font-bold">{p.seat}</span>
                <span className="min-w-0 flex-1"><b>{p.name}</b><div className="font-mono text-xs text-slate-500">{p.code} · {p.phone}</div></span>
                <StatusBadge status={p.status} cls={statusStyle[p.status]} />
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => print(p.code)} data-testid={`reprint-print-${p.code}-${p.seat}`}><Printer className="h-4 w-4" />Print</Button>
              </div>
            ))}
            {!pax.length && <p className="p-6 text-center text-sm text-slate-500">No issued tickets on this trip yet.</p>}
          </div>
        )}
      </div>
      <ReprintDialog booking={printing} onClose={() => setPrinting(null)} />
    </div>
  );
}

function MyBookings() {
  const [date, setDate] = useState(todayStr());
  const [list, setList] = useState([]);
  const [printing, setPrinting] = useState(null);
  const load = useCallback(() => api.get("/teller/bookings", { params: { date } }).then((r) => setList(r.data)), [date]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="teller-my-bookings">
      <div className="flex items-center gap-3">
        <Label className="text-xs font-semibold text-slate-600">Sold on</Label>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="my-bookings-date-input" />
        <span className="text-sm text-slate-500">{list.length} ticket(s)</span>
      </div>
      <div className="space-y-2">
        {list.map((b) => (
          <div key={b.id} className="card-soft flex flex-col gap-3 p-4 md:flex-row md:flex-wrap md:items-center" data-testid={`my-booking-row-${b.code}`}>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2"><span className="font-mono font-bold tracking-wider">{b.code}</span><StatusBadge status={b.status} cls={statusStyle[b.status]} /></div>
              <div className="text-sm font-semibold">{b.trip.origin_city} → {b.trip.destination_city} · {fmtDate(b.trip.departure)} {fmtTime(b.trip.departure)}</div>
              <div className="text-xs text-slate-500">{b.passengers.map((p) => `${p.seat}: ${p.name}`).join(" · ")} · {money(b.total)} · sold {fmtDateTime(b.created_at)}</div>
              {b.status === "cancelled" && b.cancel_reason && <div className="text-xs text-rose-600">{b.cancel_reason}{b.refund_amount ? ` · refund ${money(b.refund_amount)}` : ""}</div>}
            </div>
            <div className="flex flex-wrap gap-2">
              {["confirmed", "boarded"].includes(b.status) && <Button size="sm" variant="outline" className="rounded-full" onClick={() => setPrinting(b)} data-testid={`my-booking-reprint-${b.code}`}><Printer className="h-4 w-4" />Reprint</Button>}
              {b.status === "cancelled" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => setPrinting(b)} data-testid={`my-booking-receipt-${b.code}`}><Printer className="h-4 w-4" />Cancellation receipt</Button>}
            </div>
          </div>
        ))}
        {!list.length && <p className="card-soft p-6 text-center text-sm text-slate-500">No tickets sold on this date.</p>}
      </div>
      <ReprintDialog booking={printing} onClose={() => setPrinting(null)} />
    </div>
  );
}

function Counter({ shift, reloadShift, onClosed, user }) {
  const [date, setDate] = useState(todayStr());
  const [origin, setOrigin] = useState(shift.station_id);
  const [dest, setDest] = useState("all");
  const [trips, setTrips] = useState([]);
  const [tripId, setTripId] = useState(null);
  const [seg, setSeg] = useState({});
  const [sold, setSold] = useState(null);
  const [lookup, setLookup] = useState(false);
  const [closing, setClosing] = useState(false);
  const [manifest, setManifest] = useState(null);
  const [view, setView] = useState("sell");
  const [giveCash, setGiveCash] = useState(false);
  const [printBooking, setPrintBooking] = useState(null);
  const [autoPrint, setAutoPrint] = useState(() => localStorage.getItem("qr_teller_autoprint") === "1");
  const toggleAutoPrint = (v) => { setAutoPrint(v); localStorage.setItem("qr_teller_autoprint", v ? "1" : "0"); };
  const stations = useStations();
  const lists = useTellerLists();

  const loadTrips = useCallback(() => api.get("/public/trips/search", { params: { origin_id: origin, date, destination_id: dest === "all" ? undefined : dest } }).then((r) => setTrips(r.data)), [origin, date, dest]);
  useEffect(() => { loadTrips(); }, [loadTrips]);
  const refresh = () => { reloadShift(); loadTrips(); };

  return (
    <div className="mx-auto max-w-7xl space-y-5 px-4 py-6 sm:px-6" data-testid="teller-counter">
      <ConsoleHeader role="teller" icon={Ticket} title="Sell &amp; reprint tickets" subtitle="Open a shift, take counter sales and reprint tickets." />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="label-xs">Shift {shift.number} · opened {fmtDateTime(shift.opened_at)}</div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">{shift.station_name}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600" data-testid="teller-autoprint-toggle">
            <Printer className="h-4 w-4" />Auto print
            <Switch checked={autoPrint} onCheckedChange={toggleAutoPrint} data-testid="teller-autoprint-switch" />
          </label>
          <VoiceButton variant="outline" className="rounded-full" label="Voice booking" />
          <Button variant={view === "trips" ? "default" : "outline"} className={`rounded-full ${view === "trips" ? "bg-[#0052FF] hover:bg-[#0041CC]" : ""}`} onClick={() => setView(view === "trips" ? "sell" : "trips")} data-testid="teller-trips-button"><CalendarClock className="h-4 w-4" />Trip management</Button>
          <Button variant={view === "signout" ? "default" : "outline"} className={`rounded-full ${view === "signout" ? "bg-[#0052FF] hover:bg-[#0041CC]" : ""}`} onClick={() => setView(view === "signout" ? "sell" : "signout")} data-testid="teller-signout-button"><BusFront className="h-4 w-4" />Sign out bus</Button>
          <Button variant="outline" className="rounded-full" onClick={() => setGiveCash(true)} data-testid="teller-give-cash-button"><HandCoins className="h-4 w-4" />Give cash</Button>
          <Button variant="outline" className="rounded-full" onClick={() => setLookup(true)} data-testid="open-lookup-button"><Search className="h-4 w-4" />Find / collect</Button>
          <Button className="rounded-full bg-rose-500 hover:bg-rose-600" onClick={() => { reloadShift(); setClosing(true); }} data-testid="close-shift-button"><CloseIcon className="h-4 w-4" />Close shift</Button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Tickets sold" value={shift.tickets} icon={Ticket} testid="shift-tickets" />
        <StatCard label="Cash collected" value={money(shift.cash_total)} icon={Banknote} tint="bg-emerald-50 text-emerald-600" testid="shift-cash" />
        <StatCard label="Mobile Money" value={money(shift.momo_total)} icon={Smartphone} tint="bg-yellow-50 text-yellow-600" testid="shift-momo" />
        <StatCard label="Cash in drawer" value={money(shift.expected_cash)} icon={Wallet} tint="bg-sky-50 text-sky-600" testid="shift-expected-cash" sub={`Refunds ${money(shift.refund_total)} · Charges kept ${money(shift.cancel_fees_total || 0)}`} />
      </div>
      <div className="flex flex-wrap gap-2" data-testid="teller-tabs">
        {[["sell", "Sell tickets", Ticket], ["reprint", "Reprint", Printer], ["history", "My bookings", History], ["cancellations", "Cancellations", XCircle], ["reconcile", "Payment check", SearchCheck], ["transactions", "Cash acks", ClipboardList]].map(([v, label, Icon]) => (
          <button key={v} onClick={() => setView(v)} data-testid={`teller-tab-${v}`}
            className={`btn-press flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-bold ${view === v ? "border-[#0052FF] bg-[#0052FF] text-white" : "border-slate-200 bg-white text-slate-600"}`}><Icon className="h-4 w-4" />{label}</button>
        ))}
      </div>
      {view === "sell" && (
      <div className="grid gap-5 lg:grid-cols-[360px_1fr]">
        <div className="space-y-3">
          <IncomingHandovers onDone={refresh} />
          <IncomingCashTransfers onDone={refresh} />
          <div className="flex gap-2">
            {[0, 1, 2].map((i) => (
              <button key={i} onClick={() => setDate(todayStr(i))} data-testid={`teller-date-${i}`}
                className={`btn-press flex-1 rounded-xl border px-2 py-2 text-sm font-bold ${date === todayStr(i) ? "border-[#0052FF] bg-[#0052FF] text-white" : "border-slate-200 bg-white"}`}>{i === 0 ? "Today" : fmtDate(todayStr(i))}</button>
            ))}
          </div>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} min={todayStr()} className="w-full" data-testid="teller-sell-date-input" />
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1"><Label className="text-xs font-semibold text-slate-600">From station</Label>
              <Select value={origin} onValueChange={(v) => { setOrigin(v); setTripId(null); if (dest === v) setDest("all"); }}>
                <SelectTrigger data-testid="teller-origin-select"><SelectValue /></SelectTrigger>
                <SelectContent>{stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.city} - {s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className="text-xs font-semibold text-slate-600">To station</Label>
              <Select value={dest} onValueChange={setDest}>
                <SelectTrigger data-testid="teller-destination-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All destinations</SelectItem>{stations.filter((s) => s.id !== origin).map((s) => <SelectItem key={s.id} value={s.id}>{s.city} - {s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2" data-testid="teller-trip-list">
            {trips.map((t) => <TripCard key={t.id} trip={t} compact active={t.id === tripId} onSelect={() => { setSold(null); setTripId(t.id); setSeg({ from: t.origin_id, to: t.destination_id }); }} />)}
            {!trips.length && <p className="card-soft p-5 text-center text-sm text-slate-500">No open departures from this station.</p>}
          </div>
        </div>
        <div className="min-w-0">
          {sold ? <SoldPanel booking={sold} onNew={() => setSold(null)} />
            : tripId ? (
              <div className="space-y-3">
                <div className="flex justify-end"><Button size="sm" variant="outline" className="rounded-full" onClick={() => setManifest({ id: tripId })} data-testid="teller-manifest-button"><ClipboardList className="h-4 w-4" />Manifest</Button></div>
                <BookingFlow tripId={tripId} from={seg.from} to={seg.to} mode="teller" onBooked={(b) => { refresh(); if (autoPrint) { toast.success(`Ticket ${b.code} issued · sending to printer`); setPrintBooking(b); } else { setSold(b); } }} />
              </div>
            ) : (
              <div className="card-soft grid h-80 place-items-center p-6 text-center text-slate-500"><div><BusFront className="mx-auto h-10 w-10 text-blue-300" /><p className="mt-2 font-semibold">Select a departure to start selling</p></div></div>
            )}
        </div>
      </div>)}
      {view === "reprint" && <ReprintScreen shift={shift} />}
      {view === "history" && <MyBookings />}
      {view === "cancellations" && <TellerCancellations />}
      {view === "reconcile" && <PaymentCheck />}
      {view === "transactions" && <TransactionLedger />}
      {view === "trips" && <TripsAdmin lists={lists} />}
      {view === "signout" && <SettlementsAdmin lists={lists} />}
      <LookupDialog open={lookup} onClose={() => setLookup(false)} onTicket={setSold} onChanged={refresh} shift={shift} user={user} />
      <CloseShiftDialog shift={shift} open={closing} onClose={() => setClosing(false)} onClosed={(r) => { setClosing(false); onClosed(r); }} />
      <ManifestDialog trip={manifest} onClose={() => setManifest(null)} canRelease={false} />
      <GiveCashDialog open={giveCash} onClose={() => setGiveCash(false)} onDone={refresh} />
      <AutoPrint booking={printBooking} onDone={() => setPrintBooking(null)} />
    </div>
  );
}

export default function TellerPage() {
  const { user } = useAuth();
  const [shift, setShift] = useState(undefined);
  const [report, setReport] = useState(null);
  const load = useCallback(() => api.get("/teller/shift/current").then((r) => setShift(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const openReport = async (id) => { const { data } = await api.get(`/shifts/${id}/report`); setReport(data); };

  if (shift === undefined) return <div className="grid h-64 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#0052FF]" /></div>;
  return (
    <>
      {shift ? <Counter shift={shift} user={user} reloadShift={load} onClosed={(r) => { setReport(r); load(); }} />
        : <OpenShift user={user} onOpened={load} onReport={openReport} />}
      <ReportDialog report={report} onClose={() => setReport(null)} title="End of shift handover slip" />
    </>
  );
}
